import type { ChartMeta, ChartMetaDTO } from '@/widgets/chartsView';
import { baseApi, getErrorMessage } from '@/shared/api';
import type { CalendarDataResponse } from '../model/types';
import type {
  GetCalendarDataParams,
  GetStringFilterParams,
  GetNumberFilterParams,
} from './types';

export const getMeta = async (): Promise<ChartMeta> => {
  const { data, error } = await baseApi.functions.invoke<{
    calendarInsightsMeta: ChartMetaDTO;
  }>('getComparisonViewMeta');

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return data.calendarInsightsMeta;
};

export const getCalendarData = async ({
  split,
  metrics,
  cohortDateScale,
  cohortDateRange,
  revenueDateScale,
  revenueDateRange,
  probabilisticAttribution,
  dimensionsFilters,
  metricsFilters,
}: GetCalendarDataParams) => {
  const { data, error } = await baseApi.functions.invoke<CalendarDataResponse>(
    'getComparisonViewCalendarData',
    {
      body: {
        split,
        metrics,
        cohortDateScale,
        cohortDateRange,
        revenueDateScale,
        revenueDateRange,
        probabilisticAttribution,
        dimensionsFilters,
        metricsFilters,
      },
    },
  );

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return data;
};

export const getNumberFilterParams = async (params: GetNumberFilterParams) => {
  const { data, error } = await baseApi.functions.invoke<{
    min: number;
    max: number;
  }>('getCalendarInsightsNumberFilterParams', {
    body: {
      filterName: params.filterName,
      dimensions: params.selectedDimensions,
      dimensionsFilters: params.dimensionsFilters,
      metricsFilters: params.metricsFilters,
      cohortDateRange: params.cohortDateRange,
      cohortDateScale: params.cohortDateScale,
      revenueDateRange: params.revenueDateRange,
      revenueDateScale: params.revenueDateScale,
      probabilisticAttribution: params.probabilisticAttribution,
    },
  });

  if (!data) {
    throw new Error(getErrorMessage(error));
  }

  return data;
};

export const getStringFilterParams = async (params: GetStringFilterParams) => {
  const filtersWithoutCurrent = params.dimensionsFilters.filter((filter) => {
    return filter.id !== params.filterName;
  });

  const { data, error } = await baseApi.functions.invoke<{
    options: string[];
  }>('getCalendarInsightsStringFilterParams', {
    body: {
      filterName: params.filterName,
      dimensionsFilters: filtersWithoutCurrent,
      cohortDateRange: params.cohortDateRange,
      revenueDateRange: params.revenueDateRange,
    },
  });

  if (!data) {
    throw new Error(getErrorMessage(error));
  }

  return data;
};
